import React, { FC, useState, useEffect } from "react";
import { styled } from "@mui/system";
import { useRecoilValue } from "recoil";
import { useTheme } from "../../common/contexts/ThemeContext";

import {
  OrganizationStats,
  OutreachEmailStatuses,
  ExtendedEmailStatuses,
  EmbedOrganization,
} from "../../types/organizations";
import { COLORS, FONTS, getColors } from "../../common/consts";
import { MainChartData } from "./MainChart";
import {
  getOrganizationIdByOutreachOrgId,
  getOrganizationByOutreachOrgId,
  getChartRangeStats,
  getRangeStatsFromCache,
  type StatsResult,
} from "../../services/firebase";
import {
  getRelativeDate,
  formatLongDate,
  generateDynamicTimeframeOptions,
} from "../../common/utils/date-time";
import Card from "../../components/Display/Card";
import Text, { TextVariant } from "../../components/Display/Text";
import Select from "../../components/DataInput/Select";
import FormHelper, { Severity } from "../../components/DataInput/FormHelper";

import ProspectsTabManager from "./ProspectsTabManager";
import ActivityPanels from "./ActivityPanels";
import ReportNav, { ReportKey } from "./ReportNav";
import { useNavigate, useParams } from "react-router-dom";
import Button, { ButtonVariants } from "../../components/Display/Button";
import dayjs from "dayjs";
import { organizationAtom } from "../../state/atoms";
import { useIsHubSpotCrm } from "../../components/CrmUi/useCrm";
import HubSpotInsights from "./HubSpotInsights";

export interface TimeframeOption {
  value: string;
  label: string;
  startDate: Date;
  endDate: Date;
}

export const timeframeOptions: TimeframeOption[] = [
  {
    value: "this-week",
    label: "This week",
    startDate: getRelativeDate(new Date(), 7),
    endDate: new Date(),
  },
  {
    value: "last-week",
    label: "Last week",
    startDate: getRelativeDate(new Date(), 14),
    endDate: getRelativeDate(new Date(), 7),
  },
  {
    value: "this-month",
    label: "This month",
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
  },
  {
    value: "last-month",
    label: "Last month",
    startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
    endDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
  },
  ...generateDynamicTimeframeOptions(),
];

const StyledTitle = styled(Text)`
  line-height: 1.43;
  font-size: 1.25rem;
  font-weight: 575;
`;

const StyledSubtitle = styled(Text)`
  font-size: 14px;
  color: var(--color-foregroundSecondary, #686783);
  line-height: 1.5;
`;

const AdminBanner = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--color-borderBrandLight, ${COLORS.borderBrandLight});
  background: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight});
`;

const StyledSelect = styled(Select)`
  && {
    font: ${FONTS.sm};
  }
`;

interface Props {
  organizationId?: string;
}

const Insights: FC<Props> = ({ organizationId }) => {
  const { mode } = useTheme();
  const themeColors = getColors(mode);
  const { outreachOrganizationId } = useParams<{
    outreachOrganizationId?: string;
  }>();
  const navigate = useNavigate();
  const organizationFromAtom = useRecoilValue(organizationAtom);
  const [orgId, setOrgId] = useState<string | undefined>(organizationId);
  const [fetchedOrganization, setFetchedOrganization] = useState<EmbedOrganization | null>(null);
  const [shouldRefetchStats, setShouldRefetchStats] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>(
    timeframeOptions[0]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeReport, setActiveReport] = useState<ReportKey>("email");
  // HubSpot orgs get HubSpot's reports dashboard, which loads its own data.
  const isHubSpotLook = useIsHubSpotCrm();
  const showHubSpotDashboard = isHubSpotLook && !outreachOrganizationId;

  // The public Outreach embed has no signed-in admin, so it only gets the email report.
  const canShowActivity = !!organizationFromAtom && !outreachOrganizationId;

  // Use organization from atom if available, otherwise use fetched organization
  const organization = organizationFromAtom || fetchedOrganization;

  const [globalStats, setGlobalStats] = useState<OrganizationStats>({
    prospectsChecked: 0,
    enrichedByBoring: 0,
    bouncesPrevented: 0,
    invalid: 0,
    valid: 0,
    questionable: 0,
  });

  const [stats, setStats] = useState<MainChartData>(
    Array.from({ length: 7 }, (_, i) => ({
      date: formatLongDate(getRelativeDate(new Date(), 6 - i)),
      prospectsChecked: 0,
      [OutreachEmailStatuses.Valid]: 0,
      [OutreachEmailStatuses.Invalid]: 0,
      [OutreachEmailStatuses.Questionable]: 0,
      [ExtendedEmailStatuses.EnrichedByBoring]: 0,
      [ExtendedEmailStatuses.BouncesPrevented]: 0,
    }))
  );

  const handleTimeframeChange = (value: TimeframeOption) => {
    setSelectedTimeframe(value);
    setShouldRefetchStats(true);
  };

  useEffect(() => {
    if (organizationId || !outreachOrganizationId) {
      return;
    }

    const fetchOrgId = async (outreachOrganizationId: string) => {
      const response = await getOrganizationIdByOutreachOrgId({
        outreachOrganizationId,
      });

      if (!response?.result?.organizationId) {
        return;
      }

      setOrgId(response.result.organizationId);
    };

    fetchOrgId(outreachOrganizationId);
  }, [outreachOrganizationId, organizationId]);

  // Fetch organization data when we have outreachOrganizationId but no organization from atom
  // This is needed for non-logged-in users viewing the insights via outreach embed
  useEffect(() => {
    if (!outreachOrganizationId || organizationFromAtom) {
      return;
    }

    const fetchOrganization = async () => {
      try {
        const response = await getOrganizationByOutreachOrgId({
          outreachOrganizationId,
        });
        if (response?.result?.organization) {
          setFetchedOrganization(response.result.organization);
        }
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    };

    fetchOrganization();
  }, [outreachOrganizationId, organizationFromAtom]);

  useEffect(() => {
    if (orgId && shouldRefetchStats && !showHubSpotDashboard) {
      const getStats = async () => {
        const { startDate, endDate } = selectedTimeframe;
        setIsLoading(true);

        try {
          // Prefer server-side fetch for high-volume orgs (avoids browser crashes)
          let rangeStats: StatsResult;
          try {
            // The Outreach embed has no boring sign-in, so it asks by its
            // Outreach organization; signed in, the server checks membership.
            rangeStats = await getChartRangeStats({
              ...(outreachOrganizationId ? { outreachOrganizationId } : { organizationId: orgId || "" }),
              startDate: dayjs(startDate).startOf("day"),
              endDate: dayjs(endDate).endOf("day"),
            });
          } catch (callableError) {
            // Firestore only lets members read stats/, so the signed-out
            // Outreach embed has no direct-read fallback; it shows the error.
            if (outreachOrganizationId) throw callableError;
            rangeStats = await getRangeStatsFromCache({
              organizationId: orgId || "",
              startDate: dayjs(startDate).startOf("day"),
              endDate: dayjs(endDate).endOf("day"),
            });
          }

          const {
            prospectsChecked: prospectsCheckedDb = 0,
            valid: validFromDb = 0,
            invalid: invalidFromDb = 0,
            questionable: questionableFromDb = 0,
            enrichedByBoring: enrichedFromDb = 0,
            bouncesPrevented: bouncesPreventedFromDb = 0,
          } = rangeStats.totals || {
            prospectsChecked: 0,
            valid: 0,
            invalid: 0,
            questionable: 0,
            enrichedByBoring: 0,
            bouncesPrevented: 0,
          };

          const globalStats: OrganizationStats = {
            prospectsChecked: prospectsCheckedDb,
            enrichedByBoring: enrichedFromDb,
            valid: validFromDb,
            invalid: invalidFromDb,
            questionable: questionableFromDb,
            bouncesPrevented: bouncesPreventedFromDb,
          };

          setGlobalStats(globalStats);
          delete rangeStats["totals"];

          const sortedKeys = Object.keys(rangeStats).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime()
          );
          const stats: MainChartData = sortedKeys.map((key) => ({
            date: formatLongDate(new Date(key)),
            prospectsChecked: rangeStats[key].prospectsChecked || 0,
            [OutreachEmailStatuses.Valid]: rangeStats[key].valid || 0,
            [OutreachEmailStatuses.Invalid]: rangeStats[key].invalid || 0,
            [OutreachEmailStatuses.Questionable]:
              rangeStats[key].questionable || 0,
            [ExtendedEmailStatuses.EnrichedByBoring]:
              rangeStats[key].enrichedByBoring || 0,
            [ExtendedEmailStatuses.BouncesPrevented]:
              rangeStats[key].bouncesPrevented || 0,
          }));

          setStats(stats);
          setShouldRefetchStats(false);
          setIsLoading(false);
        } catch (error) {
          console.error("Error loading insights stats:", error);
          // Set loading to false and keep existing stats to prevent UI crash
          setIsLoading(false);
          setShouldRefetchStats(false);
          // Show error message but don't crash the UI
          setError("Unable to load latest statistics. Please refresh the page.");
          // Clear error after 5 seconds
          setTimeout(() => setError(null), 5000);
        }
      };

      // TODO:: This runs twice on initial load
      getStats();
    }
  }, [orgId, outreachOrganizationId, shouldRefetchStats, selectedTimeframe.value, showHubSpotDashboard]);

  if (showHubSpotDashboard) {
    return <HubSpotInsights organizationId={orgId} organization={organizationFromAtom} timeframeOptions={timeframeOptions} />;
  }

  return (
    <div className="d-flex flex-column gap-4">
      {outreachOrganizationId && (
        <AdminBanner>
          <div>
            <Text variant={TextVariant.md} style={{ fontWeight: 575 }}>
              {"You're viewing the email report only"}
            </Text>
            <StyledSubtitle variant={TextVariant.sm} style={{ marginBottom: 0 }}>
              Log in as an admin to see profile, phone, and LinkedIn activity and manage settings.
            </StyledSubtitle>
          </div>
          <Button
            variant={ButtonVariants.brand}
            onClick={() => navigate("/auth/login")}
          >
            Log in as admin
          </Button>
        </AdminBanner>
      )}
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-top">
          <div>
            <StyledTitle variant={TextVariant.xl}>
              Insights
            </StyledTitle>
            <StyledSubtitle 
              variant={TextVariant.sm}
              style={{ marginBottom: 0 }}
            >
              How boring is performing across email, profile, phone, and LinkedIn data
            </StyledSubtitle>
          </div>
          <div className="d-flex align-items-center">
            <StyledSelect
              value={selectedTimeframe.value}
              options={timeframeOptions}
              onChange={(e) => {
                const { value } = e.target;
                const selectedOption = timeframeOptions.find(
                  (option) => option.value === value
                );
                if (selectedOption) {
                  handleTimeframeChange(selectedOption);
                }
              }}
            />
          </div>
        </div>
        {error && (
          <FormHelper helperText={error} severity={Severity.error} />
        )}
      </Card>
      {canShowActivity && <ReportNav value={activeReport} onChange={setActiveReport} />}
      {activeReport === "email" ? (
        <ProspectsTabManager
          globalStats={globalStats}
          stats={stats}
          isLoading={isLoading}
          organization={organization}
          timeframe={selectedTimeframe}
        />
      ) : (
        <ActivityPanels report={activeReport} timeframe={selectedTimeframe} enabled={canShowActivity} />
      )}
    </div>
  );
};

export default Insights;
